import type {RouteObject as ReactRouterRouteObject} from '../react-router'
import type {GetRouteOptions, TanStackRouteOptions} from './types'

/**
 * Converts a React Router `RouteObject` into the options object passed to TanStack Router's
 * `createRoute` or `createFileRoute`.
 *
 * The adapter auto-converts these TanStack route properties from the React Router route:
 *
 * - `beforeLoad`: initialized to return `{dataRouterId: routeObj.id}`.
 * - `shouldReload`: derived via `shouldReloadAdapter(...)`.
 * - `staticData`: initialized with `{reactRouterHandle: routeObj.handle}`.
 *
 * When `getRouteOptions` is provided, its return value is merged on top of the adapter-generated
 * options. `beforeLoad` and `staticData` are augmented rather than replaced: `beforeLoad` merges
 * the consumer result with the adapter's `{dataRouterId}` payload, and `staticData` shallow-merges
 * onto `{reactRouterHandle: routeObj.handle}`. Other overlapping properties override the adapter
 * value.
 */
export function buildTanStackRouteOptions(
  routeObj: ReactRouterRouteObject,
  getRouteOptions?: GetRouteOptions,
): TanStackRouteOptions {
  const customOptions = getRouteOptions ? getRouteOptions(routeObj) : {}

  const adapterOptions = {
    beforeLoad: () => ({dataRouterId: routeObj.id}),
    shouldReload: shouldReloadAdapter(routeObj, customOptions),
    staticData: {reactRouterHandle: routeObj.handle},
  }
  if (getRouteOptions) {
    // Merge consumer-provided route options with adapter-generated options.
    const {staticData: customStaticData, beforeLoad: customBeforeLoad, ...otherCustomOptions} = customOptions
    return {
      ...adapterOptions,
      ...(customStaticData ? {staticData: {...adapterOptions.staticData, ...customStaticData}} : {}),
      ...(customBeforeLoad ? {beforeLoad: beforeLoadAdapter(adapterOptions.beforeLoad, customBeforeLoad)} : {}),
      ...otherCustomOptions,
    }
  }

  return adapterOptions
}

function beforeLoadAdapter(
  adapterBeforeLoad: NonNullable<TanStackRouteOptions['beforeLoad']>,
  customBeforeLoad: NonNullable<TanStackRouteOptions['beforeLoad']>,
): NonNullable<TanStackRouteOptions['beforeLoad']> {
  return params => {
    const beforeLoadResult = customBeforeLoad(params)
    // If the consumer's beforeLoad returns a promise, wait for it to resolve before
    // merging its result with the adapter's beforeLoad result.
    if (isPromise(beforeLoadResult)) {
      return (async () => {
        const resolvedBeforeLoadResult = await beforeLoadResult
        return {...adapterBeforeLoad(params), ...(resolvedBeforeLoadResult ?? {})}
      })()
    }
    return {...adapterBeforeLoad(params), ...(beforeLoadResult ?? {})}
  }
}

/**
 * This matches the guard used internally by TanStack Router to detect if beforeLoad returns a promise.
 * https://github.com/TanStack/router/blob/14520e5eb5f5a08026fdb46292fe35cf428a87aa/packages/router-core/src/load-matches.ts#L518
 */
function isPromise<T>(value: Promise<Awaited<T>> | T): value is Promise<Awaited<T>> {
  return Boolean(value && typeof value === 'object' && typeof (value as Promise<T>)['then'] === 'function')
}

/**
 * Passes through the consumer-provided TanStack Router `shouldReload` option.
 * `shouldReload` is optional unless the source React Router route defines
 * `shouldRevalidate`, in which case this adapter throws unless an explicit
 * `shouldReload` adapter is provided.
 *
 * React Router's `shouldRevalidate` receives a richer set of navigation and
 * submission details than TanStack Router's `shouldReload`, so there is no
 * safe generic adapter between the two. Routes that define
 * `routeObj.shouldRevalidate` must therefore provide an explicit
 * `shouldReload` option via the `getRouteOptions` callback.
 *
 * See:
 * https://tanstack.com/router/latest/docs/framework/react/api/router/RouteOptionsType#shouldreload-property
 */
function shouldReloadAdapter(
  routeObj: ReactRouterRouteObject,
  customOptions: TanStackRouteOptions,
): TanStackRouteOptions['shouldReload'] {
  if (routeObj.shouldRevalidate && !('shouldReload' in customOptions)) {
    throw new Error(
      `Route with id "${routeObj.id}" has a shouldRevalidate function but no shouldReload option. ` +
        `React Router's shouldRevalidate cannot be automatically adapted for TanStack. ` +
        `Please configure shouldReload for this route.`,
    )
  }
  return customOptions['shouldReload']
}
